import { v4 as uuid } from 'uuid';

import { jotaiStore } from '@shared/store/jotai/jotai-store';

import { alertAtom } from '@shared/store/slices/alert.atom';

export type { AlertItem } from '@shared/store/slices/alert.atom';

const PERSISTENT_ALERT_MESSAGES_KEY = 'persistentAlertMessages';

const readPersistentMessages = (): string[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PERSISTENT_ALERT_MESSAGES_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const rememberPersistentMessage = (msg: unknown) => {
  if (typeof msg !== 'string' || !msg) return;

  const messages = readPersistentMessages();
  if (messages.includes(msg)) return;

  try {
    sessionStorage.setItem(PERSISTENT_ALERT_MESSAGES_KEY, JSON.stringify([...messages, msg]));
  } catch {
    // sessionStorage can be unavailable
  }
};

export const persistentAlertTimeout = (msg: unknown): number | null | undefined =>
  typeof msg === 'string' && readPersistentMessages().includes(msg) ? null : undefined;

export const dismissAlert = (id: string) => {
  let removedMessage: unknown;

  jotaiStore.set(alertAtom, (prev) => {
    removedMessage = prev.find((alertItem) => alertItem.id === id)?.msg;
    return prev.filter((alertItem) => alertItem.id !== id);
  });

  if (typeof removedMessage !== 'string') return;

  try {
    const messages = readPersistentMessages().filter((item) => item !== removedMessage);
    sessionStorage.setItem(PERSISTENT_ALERT_MESSAGES_KEY, JSON.stringify(messages));
  } catch {
    // sessionStorage can be unavailable
  }
};

export const clearAlerts = () => {
  jotaiStore.set(alertAtom, []);
  try {
    sessionStorage.removeItem(PERSISTENT_ALERT_MESSAGES_KEY);
  } catch {
    // sessionStorage can be unavailable
  }
};

type AlertOptions = {
  dismissible?: boolean;
};

// Pass null to keep the alert until it is replaced or cleared.
export const setAlert = async (
  msg: any,
  alertType: string,
  timeout: number | null = 8000,
  options?: AlertOptions
) => {
  const id = uuid();

  if (timeout == null) {
    rememberPersistentMessage(msg);
    jotaiStore.set(alertAtom, (prev) => {
      const withoutDuplicate = prev.filter((alertItem) => alertItem.msg !== msg);
      return [
        ...withoutDuplicate,
        { msg, alertType, id, persistent: true, dismissible: !!options?.dismissible },
      ];
    });
    return;
  }

  let added = false;
  jotaiStore.set(alertAtom, (prev) => {
    if (prev.some((alertItem) => alertItem.persistent && alertItem.msg === msg)) {
      return prev;
    }
    added = true;
    return [...prev, { msg, alertType, id }];
  });

  if (!added) return;

  setTimeout(() => {
    jotaiStore.set(alertAtom, (prev) => prev.filter((alertItem) => alertItem.id !== id));
  }, timeout);
};
